import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import * as Sentry from '@sentry/browser';

/**
 * useBeerDelete
 * Provides a callable-based delete operation for beers.
 *
 * @returns {{ deleteBeers: Function, loading: boolean, errorMessage: string, clearError: Function }}
 */
export function useBeerDelete() {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const clearError = useCallback(() => {
    setErrorMessage('');
  }, []);

  /**
   * Delete one or more beers.
   *
   * @param {string[]} beerIds - Array of beer document IDs to delete.
   * @returns {Promise<{ deleted: boolean, deletedCount: number, failedCount: number, failedBeers: Array, status: string, error?: string }>}
   */
  const deleteBeers = useCallback(async (beerIds) => {
    if (!Array.isArray(beerIds)) {
      return { deleted: false, deletedCount: 0, failedCount: 0, failedBeers: [], status: '' };
    }

    const ids = beerIds.filter(
      (id) => typeof id === 'string' && id.trim() !== '',
    );
    if (!ids.length) {
      return { deleted: false, deletedCount: 0, failedCount: 0, failedBeers: [], status: '' };
    }

    if (!isMountedRef.current) {
      return { deleted: false, deletedCount: 0, failedCount: 0, failedBeers: [], status: '' };
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const deleteBeersFn = httpsCallable(
        functions,
        'callableBeerDeleteBeers-deleteBeers',
      );
      const result = await deleteBeersFn({
        beerIds: ids,
      });

      const response = result?.data || {};
      const deletedCount = response.deletedCount || 0;
      const failedCount = response.failedCount || 0;
      const failedBeers = Array.isArray(response.failedBeers)
        ? response.failedBeers
        : [];

      return {
        deleted: deletedCount > 0,
        deletedCount,
        failedCount,
        failedBeers,
        status: response.status || '',
      };
    } catch (err) {
      Sentry.captureException(err);
      if (isMountedRef.current) {
        const friendly =
          'Failed to delete beers. Please try again or contact support.';
        setErrorMessage(friendly);
        return {
          deleted: false,
          deletedCount: 0,
          failedCount: 0,
          failedBeers: [],
          status: '',
          error: friendly,
        };
      }
      return {
        deleted: false,
        deletedCount: 0,
        failedCount: 0,
        failedBeers: [],
        status: '',
      };
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  return {
    deleteBeers,
    loading,
    errorMessage,
    clearError,
  };
}
